/**
 * Build Table Rows - Transform placement data for table display
 *
 * Converts AutoPark algorithm output into a tabular format optimized for the PlacementTableView.
 * Each row represents one aircraft with its placement status, spot assignment(s), and timing information.
 */

import { addHours } from "date-fns";
import {
  Placement,
  ParkingRequest,
  Spot,
  SizeCategory,
} from "../../../screens/Ramp/TemporalTest/types";
import {
  formatDuration,
  PartialPlacementInfo,
} from "./detectPartialPlacements";

/**
 * Status of an aircraft's placement
 */
export type PlacementStatus = 'placed' | 'partial' | 'unplaced';

/**
 * Table row representing one aircraft and its placement
 */
export interface TableRow {
  // Identity
  aircraftId: string;
  tailNumber: string;
  aircraftModel: string;
  aircraftMake: string;
  sizeCategory: SizeCategory;

  // Placement status
  status: PlacementStatus;
  spotSummary: string; // "M1" or "M1 → F3 → M1" or "—"
  spotIds: string[]; // All spot IDs involved
  moveCount: number; // Number of moves (0 for single placement)

  // Timing
  arrivalBucket: number;
  departureBucket: number;
  arrivalTime: Date;
  departureTime: Date;
  dwellMinutes: number;
  dwellFormatted: string; // "3h 30m" or "1d 5h"

  // Additional metadata
  segments?: Array<{
    spotId: string;
    startBucket: number;
    endBucket: number;
  }>;
  partialInfo?: PartialPlacementInfo;
  placementSource?: 'manual' | 'auto' | 'import';
  isLocked?: boolean;
}

/**
 * Convert time bucket to Date
 */
function bucketToDate(bucket: number, baseDate: Date): Date {
  return addHours(baseDate, bucket * 0.25); // 15-minute buckets
}

/**
 * Format spot summary for display
 * - Single spot: "M1"
 * - 2-3 spots: "M1 → F3 → M1"
 * - 4+ spots: "M1 + 3 more"
 */
function formatSpotSummary(spotIds: string[]): string {
  if (spotIds.length === 0) return "—";
  if (spotIds.length === 1) return spotIds[0];
  if (spotIds.length <= 3) return spotIds.join(" → ");
  return `${spotIds[0]} + ${spotIds.length - 1} more`;
}

/**
 * Build table rows from placement data
 *
 * @param aircraft - Parking requests (all aircraft)
 * @param placement - Placement result from algorithm
 * @param startTime - Base date for time bucket conversion
 * @param partialPlacements - Detected partial placements
 * @param spotIdToName - Map spot IDs to display names
 * @returns Array of table rows
 */
export function buildTableRows(
  aircraft: ParkingRequest[],
  placement: Placement,
  startTime: Date,
  partialPlacements: PartialPlacementInfo[],
  spotIdToName: Record<string, string>
): TableRow[] {
  return aircraft.map((ac) => {
    const placed = placement[ac.id];
    const partial = partialPlacements.find((p) => p.aircraftId === ac.id);

    // Determine status
    let status: PlacementStatus;
    if (!placed) {
      status = 'unplaced';
    } else if (partial) {
      status = 'partial';
    } else {
      status = 'placed';
    }

    // Extract spot information
    let spotIds: string[] = [];
    let spotSummary: string;
    let moveCount = 0;
    let segments: Array<{ spotId: string; startBucket: number; endBucket: number }> | undefined;

    if (!placed) {
      // Unplaced
      spotIds = [];
      spotSummary = "—";
    } else if ("segments" in placed) {
      // Multi-segment placement
      segments = placed.segments.map((seg) => ({
        spotId: seg.spotId,
        startBucket: seg.startBucket,
        endBucket: seg.endBucket,
      }));
      spotIds = segments.map((s) => s.spotId);
      moveCount = placed.moveCount;

      // Use display names if available
      const displayNames = spotIds.map((id) => spotIdToName[id] || id);
      spotSummary = formatSpotSummary(displayNames);
    } else {
      // Single placement
      spotIds = [placed.spotId];
      const displayName = spotIdToName[placed.spotId] || placed.spotId;
      spotSummary = displayName;

      // Create single segment for display
      segments = [{
        spotId: placed.spotId,
        startBucket: ac.arrivalBucket,
        endBucket: ac.departureBucket,
      }];
    }

    // Calculate timing
    const arrivalTime = bucketToDate(ac.arrivalBucket, startTime);
    const departureTime = bucketToDate(ac.departureBucket, startTime);
    const dwellMinutes = (ac.departureBucket - ac.arrivalBucket) * 15;
    const dwellFormatted = formatDuration(dwellMinutes);

    // Extract aircraft information
    const aircraftMake = ac.aircraft?.make || "";
    const aircraftModel = ac.aircraft?.model || "Unknown";

    return {
      // Identity
      aircraftId: ac.id,
      tailNumber: ac.tail_number,
      aircraftModel,
      aircraftMake,
      sizeCategory: ac.sizeCategory,

      // Placement status
      status,
      spotSummary,
      spotIds,
      moveCount,

      // Timing
      arrivalBucket: ac.arrivalBucket,
      departureBucket: ac.departureBucket,
      arrivalTime,
      departureTime,
      dwellMinutes,
      dwellFormatted,

      // Additional metadata
      segments,
      partialInfo: partial,
      placementSource: ac.placement_source,
      isLocked: ac.is_locked,
    };
  });
}

/**
 * Filter rows by status
 */
export function filterByStatus(
  rows: TableRow[],
  filterMode: 'all' | 'placed' | 'unplaced'
): TableRow[] {
  if (filterMode === 'all') return rows;
  if (filterMode === 'placed') return rows.filter((r) => r.status === 'placed' || r.status === 'partial');
  if (filterMode === 'unplaced') return rows.filter((r) => r.status === 'unplaced');
  return rows;
}

/**
 * Filter rows by search query (tail number)
 */
export function filterBySearch(rows: TableRow[], query: string): TableRow[] {
  if (!query.trim()) return rows;
  const lowerQuery = query.toLowerCase();
  return rows.filter((r) => r.tailNumber.toLowerCase().includes(lowerQuery));
}

/**
 * Filter rows by zone
 */
export function filterByZone(
  rows: TableRow[],
  zoneFilter: string | null,
  spotToZone: Record<string, string>
): TableRow[] {
  if (!zoneFilter) return rows;

  return rows.filter((r) => {
    // Check if any of the row's spots belong to the selected zone
    return r.spotIds.some((spotId) => spotToZone[spotId] === zoneFilter);
  });
}

/**
 * Sort rows by column
 */
export function sortRows(
  rows: TableRow[],
  sortBy: 'status' | 'tail' | 'spot' | 'arrival' | 'departure',
  sortOrder: 'asc' | 'desc'
): TableRow[] {
  const sorted = [...rows].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'status':
        // Order: unplaced → partial → placed
        const statusOrder = { unplaced: 0, partial: 1, placed: 2 };
        comparison = statusOrder[a.status] - statusOrder[b.status];
        break;

      case 'tail':
        comparison = a.tailNumber.localeCompare(b.tailNumber);
        break;

      case 'spot':
        comparison = a.spotSummary.localeCompare(b.spotSummary);
        break;

      case 'arrival':
        comparison = a.arrivalBucket - b.arrivalBucket;
        break;

      case 'departure':
        comparison = a.departureBucket - b.departureBucket;
        break;
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });

  return sorted;
}

/**
 * Format size category as human-readable label
 */
export function formatSizeCategory(size: SizeCategory): string {
  const labels: Record<SizeCategory, string> = {
    "very-small": "VS",
    "small": "S",
    "midsize": "M",
    "super-midsize": "SM",
    "large": "L",
    "enormous": "E",
  };
  return labels[size] || size;
}
